import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import FormContext from '../../../form-context';
import {
    isAllLocationsGroupKey,
    resolveReviewsLinkAction
} from './reviews-link-action-rules';
import {
    ALL_LOCATIONS_SOURCE_VALUE,
    buildReviewSourceOptions,
    doesClientAddSelectableOptions,
    getSourceOptions,
    isSimpleSingleLocationState,
    resolveSelectionCount,
    toConfigPatch,
    toReviewSourceValue
} from './reviews-source-selection-rules';
import {
    getGoogleReviewsSetupState,
    GOOGLE_REVIEWS_SETUP_MESSAGE,
    GOOGLE_REVIEWS_SETUP_STATES
} from './google-reviews-setup-state';

const SCOPES = ['website', 'client'];
const GOOGLE_REVIEWS_SYNC_MESSAGE = 'Google reviews are still syncing. Changes can take up to 1 hour to appear.';

const areSameArray = (left, right) => {
    if (!Array.isArray(left) || !Array.isArray(right)) {
        return false;
    }

    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => value === right[index]);
};

const hasValueChanged = (current, next) => {
    if (Array.isArray(current) || Array.isArray(next)) {
        return !areSameArray(current, next);
    }

    return current !== next;
};

const defaultScopeState = {
    website: [],
    client: []
};

const withSafeScope = (value) => (SCOPES.includes(value) ? value : 'website');
const unfilteredGroupKeys = new Set(['all', 'default']);

const toGroupLabel = (groupKey) => {
    const normalized = String(groupKey || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!normalized) {
        return '';
    }

    return normalized.replace(/\b\w/g, character => character.toUpperCase());
};

const buildGroupOptionsFromSources = (sources = []) => {
    const groupsByKey = new Map();

    (Array.isArray(sources) ? sources : []).forEach((source) => {
        const reviewGroups = Array.isArray(source?.reviewGroups) ? source.reviewGroups : [];
        reviewGroups.forEach((groupKeyValue) => {
            const groupKey = String(groupKeyValue || '').trim();
            if (!groupKey) {
                return;
            }

            const normalizedKey = groupKey.toLowerCase();
            if (unfilteredGroupKeys.has(normalizedKey) || groupsByKey.has(normalizedKey)) {
                return;
            }

            groupsByKey.set(normalizedKey, {
                value: groupKey,
                label: toGroupLabel(groupKey)
            });
        });
    });

    return [
        {value: 'all', label: 'All locations'},
        ...Array.from(groupsByKey.values()).sort((left, right) => left.label.localeCompare(right.label))
    ];
};

const resolveHasMultipleClientWorkspaces = (formContext) => {
    const contextValue = formContext?.appContext?.workspaceConfig?.hasMultipleClientWorkspaces;
    if (typeof contextValue === 'boolean') {
        return contextValue;
    }

    const globalValue = globalThis?.__workspaceConfig?.hasMultipleClientWorkspaces;
    if (typeof globalValue === 'boolean') {
        return globalValue;
    }

    return null;
};

const fetchHasMultipleClientWorkspaces = async (signal) => {
    try {
        const configurationResponse = await fetch('/api/v2/workspaces/current/configuration', {signal});
        if (!configurationResponse.ok) {
            return true;
        }

        const configuration = await configurationResponse.json();
        return Boolean(configuration?.hasMultipleClientWorkspaces);
    } catch (error) {
        if (error.name === 'AbortError') {
            throw error;
        }

        return true;
    }
};

const loadScopeSources = async (scope, signal) => {
    const sourcesResponse = await fetch(`/api/v2/reviews/sources?scope=${encodeURIComponent(scope)}`, {
        signal
    });

    const sources = sourcesResponse.ok
        ? await sourcesResponse.json()
        : [];
    const normalizedSources = Array.isArray(sources) ? sources : [];

    return {
        scope,
        groups: buildGroupOptionsFromSources(normalizedSources),
        sources: normalizedSources
    };
};

const ReviewsBrickPanel = ({config, setConfig}) => {
    const formContext = useContext(FormContext);
    const [groupOptionsByScope, setGroupOptionsByScope] = useState(defaultScopeState);
    const [sourceOptionsByScope, setSourceOptionsByScope] = useState(defaultScopeState);
    const [loading, setLoading] = useState(true);
    const [reviewsSetupState, setReviewsSetupState] = useState(GOOGLE_REVIEWS_SETUP_STATES.unknown);
    const [isCheckingSetup, setIsCheckingSetup] = useState(false);
    const previousSelectionKeyRef = useRef(null);

    useEffect(() => {
        const controller = new AbortController();

        const loadScopeOptions = async () => {
            try {
                setLoading(true);
                const websiteScopeRequest = loadScopeSources('website', controller.signal);
                const cachedWorkspaceScope = resolveHasMultipleClientWorkspaces(formContext);
                const hasMultipleClientWorkspaces = cachedWorkspaceScope === null
                    ? await fetchHasMultipleClientWorkspaces(controller.signal)
                    : cachedWorkspaceScope;
                const scopeRequests = [websiteScopeRequest];
                if (hasMultipleClientWorkspaces) {
                    scopeRequests.push(loadScopeSources('client', controller.signal));
                }

                const loaded = await Promise.all(scopeRequests);
                const nextGroupsByScope = {...defaultScopeState};
                const nextSourcesByScope = {...defaultScopeState};

                loaded.forEach(({scope, groups, sources}) => {
                    nextGroupsByScope[scope] = groups;
                    nextSourcesByScope[scope] = sources;
                });

                setGroupOptionsByScope(nextGroupsByScope);
                setSourceOptionsByScope(nextSourcesByScope);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setGroupOptionsByScope(defaultScopeState);
                    setSourceOptionsByScope(defaultScopeState);
                }
            } finally {
                setLoading(false);
            }
        };

        loadScopeOptions();

        return () => controller.abort();
    }, []);

    const updateSetupState = useCallback(async (shouldApply = () => true) => {
        if (shouldApply()) {
            setIsCheckingSetup(true);
        }

        try {
            const state = await getGoogleReviewsSetupState();
            if (shouldApply()) {
                setReviewsSetupState(state);
            }
            return state;
        } finally {
            if (shouldApply()) {
                setIsCheckingSetup(false);
            }
        }
    }, []);

    const refreshSetupState = async () => updateSetupState();

    useEffect(() => {
        let active = true;
        const isActive = () => active;
        const safeRefresh = () => updateSetupState(isActive);

        const onSetupUpdated = () => {
            safeRefresh();
        };

        safeRefresh();
        window.addEventListener('cx:google-reviews-settings-updated', onSetupUpdated);
        return () => {
            active = false;
            window.removeEventListener('cx:google-reviews-settings-updated', onSetupUpdated);
        };
    }, [updateSetupState]);

    const configuredScope = withSafeScope(config?.scope || 'website');
    const hasClientExtraOptions = useMemo(() => {
        return doesClientAddSelectableOptions({
            websiteGroupOptions: groupOptionsByScope.website,
            websiteSourceOptions: sourceOptionsByScope.website,
            clientGroupOptions: groupOptionsByScope.client,
            clientSourceOptions: sourceOptionsByScope.client
        });
    }, [groupOptionsByScope, sourceOptionsByScope]);

    const hasLoadedScopes = !loading;
    const showScopeSelect = !loading && hasClientExtraOptions;
    const effectiveScope = showScopeSelect ? configuredScope : 'website';

    const activeGroupOptions = groupOptionsByScope[effectiveScope] || [];
    const activeSourceOptions = sourceOptionsByScope[effectiveScope] || [];
    const sourceOptionState = useMemo(() => {
        return buildReviewSourceOptions({
            groupOptions: activeGroupOptions,
            sourceOptions: activeSourceOptions
        });
    }, [activeGroupOptions, activeSourceOptions]);

    const selectedReviewSourceValue = toReviewSourceValue(config);
    const simpleSingleLocation = isSimpleSingleLocationState({
        scope: effectiveScope,
        groupOptions: activeGroupOptions,
        sourceOptions: activeSourceOptions
    });
    const singleLocationOption = getSourceOptions(activeSourceOptions)[0];

    const applyPatch = (patch) => {
        setConfig(previous => {
            let changed = false;
            const next = {...previous};

            Object.entries(patch).forEach(([fieldName, nextValue]) => {
                if (hasValueChanged(previous[fieldName], nextValue)) {
                    next[fieldName] = nextValue;
                    changed = true;
                }
            });

            return changed ? next : previous;
        });
    };

    useEffect(() => {
        if (loading) {
            return;
        }

        if (!showScopeSelect && configuredScope !== 'website') {
            applyPatch({scope: 'website'});
        }
    }, [loading, showScopeSelect, configuredScope]);

    useEffect(() => {
        if (loading) {
            return;
        }

        if (simpleSingleLocation && singleLocationOption) {
            const targetValue = `source:${singleLocationOption.value}`;
            if (selectedReviewSourceValue !== targetValue) {
                applyPatch({
                    scope: effectiveScope,
                    ...toConfigPatch(targetValue)
                });
            }
            return;
        }

        if (!sourceOptionState.isValidValue(selectedReviewSourceValue)) {
            applyPatch({
                scope: effectiveScope,
                ...toConfigPatch(ALL_LOCATIONS_SOURCE_VALUE)
            });
        }
    }, [
        loading,
        simpleSingleLocation,
        singleLocationOption?.value,
        selectedReviewSourceValue,
        sourceOptionState,
        effectiveScope
    ]);

    const selectionCount = useMemo(() => {
        if (loading) {
            return null;
        }

        return resolveSelectionCount({
            config,
            sourceOptions: activeSourceOptions
        });
    }, [loading, config, activeSourceOptions]);

    const groupKey = String(config?.groupKey || '').trim();
    const mode = String(config?.mode || '').trim().toLowerCase();
    const sourceKey = String(config?.sourceKey || '').trim();
    const isAllLocationsGroup = !sourceKey && mode !== 'single' && isAllLocationsGroupKey(groupKey || ALL_LOCATIONS_SOURCE_VALUE);
    const actionState = resolveReviewsLinkAction({
        configuredLinkTarget: config?.linkTarget,
        legacyLinkEnabled: config?.link !== false,
        selectionCount,
        isAllLocationsGroup
    });

    const hasKnownSelection = loading === false
        && (
            !!sourceKey
            || sourceOptionState.isValidValue(selectedReviewSourceValue)
        );

    useEffect(() => {
        const normalizedConfiguredLinkTarget = String(config?.linkTarget || '').trim().toLowerCase();
        if (
            normalizedConfiguredLinkTarget === 'google'
            && actionState.nextLinkTarget !== 'google'
            && hasKnownSelection
        ) {
            applyPatch({linkTarget: 'none'});
        }
    }, [config?.linkTarget, actionState.nextLinkTarget, hasKnownSelection]);

    const resolvedLinkTarget = actionState.nextLinkTarget;
    const displayReviewSourceValue = sourceOptionState.isValidValue(selectedReviewSourceValue)
        ? selectedReviewSourceValue
        : ALL_LOCATIONS_SOURCE_VALUE;
    const showSetupWarning = reviewsSetupState === GOOGLE_REVIEWS_SETUP_STATES.notConfigured;

    useEffect(() => {
        if (loading) {
            return;
        }

        const selectionKey = `${effectiveScope}|${displayReviewSourceValue}`;
        const selectionHasChanged = previousSelectionKeyRef.current !== selectionKey;
        previousSelectionKeyRef.current = selectionKey;

        if (!selectionHasChanged) {
            return;
        }

        const currentLinkTarget = String(config?.linkTarget || '').trim().toLowerCase();
        if (currentLinkTarget !== 'none') {
            return;
        }

        if (effectiveScope !== 'website') {
            return;
        }

        if (Number(selectionCount) !== 1) {
            return;
        }

        applyPatch({linkTarget: 'google'});
    }, [loading, effectiveScope, displayReviewSourceValue, config?.linkTarget, selectionCount]);

    return (
        <>
            {showSetupWarning ? (
                <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                    <div>{GOOGLE_REVIEWS_SETUP_MESSAGE}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        <a
                            href="/settings/integrations"
                            className="underline font-medium text-amber-900 hover:text-amber-950"
                        >
                            Open Apps & Integrations
                        </a>
                        <a
                            href="/settings/business/locations"
                            className="underline font-medium text-amber-900 hover:text-amber-950"
                        >
                            Open Business Locations
                        </a>
                    </div>
                    <button
                        type="button"
                        className="mt-1 inline-flex items-center rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={refreshSetupState}
                        disabled={isCheckingSetup}
                    >
                        {isCheckingSetup ? 'Rechecking...' : 'Recheck'}
                    </button>
                </div>
            ) : null}

            {hasLoadedScopes ? (
                <>
                    {showScopeSelect ? (
                        <>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
                            <select
                                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                                value={effectiveScope}
                                onChange={(event) => {
                                    applyPatch({scope: withSafeScope(event.target.value)});
                                }}
                                aria-label="Scope"
                            >
                                <option value="website">This website</option>
                                <option value="client">All client workspaces</option>
                            </select>
                        </>
                    ) : null}

                    {!simpleSingleLocation ? (
                        <>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Review source</label>
                            <select
                                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                                value={displayReviewSourceValue}
                                onChange={(event) => {
                                    applyPatch({
                                        scope: effectiveScope,
                                        ...toConfigPatch(event.target.value)
                                    });
                                }}
                                disabled={loading}
                                aria-label="Review source"
                            >
                                <option value={sourceOptionState.allOption.value}>{sourceOptionState.allOption.label}</option>
                                {sourceOptionState.groupSelectionOptions.length > 0 ? (
                                    <optgroup label="Review groups">
                                        {sourceOptionState.groupSelectionOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </optgroup>
                                ) : null}
                                {sourceOptionState.locationSelectionOptions.length > 0 ? (
                                    <optgroup label="Locations">
                                        {sourceOptionState.locationSelectionOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </optgroup>
                                ) : null}
                            </select>
                        </>
                    ) : null}
                </>
            ) : null}

            {!showSetupWarning ? (
                <div className="mb-2 rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs text-sky-800">
                    {GOOGLE_REVIEWS_SYNC_MESSAGE}
                </div>
            ) : null}

            <label className="block text-xs font-medium text-gray-600 mb-1">Style</label>
            <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                value={config.variant}
                onChange={(e) => setConfig(prev => ({...prev, variant: e.target.value}))}
            >
                <option value="inline">Inline</option>
                <option value="pill">Pill</option>
                <option value="outline">Outline</option>
            </select>

            <label className="block text-xs font-medium text-gray-600 mb-1">Rating precision</label>
            <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                value={config.precision}
                onChange={(e) => setConfig(prev => ({...prev, precision: Number(e.target.value)}))}
            >
                <option value={0}>0 decimal places</option>
                <option value={1}>1 decimal place</option>
                <option value={2}>2 decimal places</option>
            </select>

            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
                Show Google logo
                <input
                    type="checkbox"
                    checked={config.logo}
                    onChange={(e) => setConfig(prev => ({...prev, logo: e.target.checked}))}
                />
            </label>

            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
                Show rating number
                <input
                    type="checkbox"
                    checked={config.showRating}
                    onChange={(e) => setConfig(prev => ({...prev, showRating: e.target.checked}))}
                />
            </label>

            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
                Show stars
                <input
                    type="checkbox"
                    checked={config.showStars}
                    onChange={(e) => setConfig(prev => ({...prev, showStars: e.target.checked}))}
                />
            </label>

            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
                Show count
                <input
                    type="checkbox"
                    checked={config.showCount}
                    onChange={(e) => setConfig(prev => ({...prev, showCount: e.target.checked}))}
                />
            </label>

            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
                Count in parentheses
                <input
                    type="checkbox"
                    checked={config.parenCount}
                    onChange={(e) => setConfig(prev => ({...prev, parenCount: e.target.checked}))}
                    disabled={!config.showCount}
                />
            </label>

            <label className="block text-xs font-medium text-gray-600 mb-1">Count format</label>
            <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                value={config.countFormat}
                onChange={(e) => setConfig(prev => ({...prev, countFormat: e.target.value}))}
                disabled={!config.showCount}
            >
                <option value="full">Full numbers</option>
                <option value="short">Short (K/M)</option>
                <option value="auto">Auto</option>
            </select>

            <label className="block text-xs font-medium text-gray-600 mb-1">Copy</label>
            <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                value={config.copyStyle}
                onChange={(e) => setConfig(prev => ({...prev, copyStyle: e.target.value}))}
                aria-label="Copy"
            >
                <option value="on-google">on Google</option>
                <option value="google-reviews">Google reviews</option>
                <option value="custom">Custom text</option>
                <option value="none">None</option>
            </select>

            {config.copyStyle === 'custom' ? (
                <>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom copy</label>
                    <input
                        type="text"
                        className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                        value={config.copyText || ''}
                        onChange={(e) => setConfig(prev => ({...prev, copyText: e.target.value}))}
                        placeholder="Reviews"
                        aria-label="Custom copy"
                    />
                </>
            ) : null}

            <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
            <select
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs mb-2"
                value={resolvedLinkTarget}
                onChange={(e) => setConfig(prev => ({...prev, linkTarget: e.target.value}))}
                aria-label="Action"
            >
                {actionState.availableActions.includes('google') ? (
                    <option value="google">Open Google</option>
                ) : null}
                <option value="custom">Open custom link</option>
                <option value="none">No link</option>
            </select>

            {resolvedLinkTarget === 'custom' ? (
                <>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom URL</label>
                    <input
                        type="text"
                        className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
                        value={config.customUrl || ''}
                        onChange={(e) => setConfig(prev => ({...prev, customUrl: e.target.value}))}
                        placeholder="https://example.com/reviews"
                    />
                </>
            ) : null}
        </>
    );
};

export default ReviewsBrickPanel;
